export function defaultExpressionResolver(regex, expression, newFunctionName) {
  return expression.replace(regex, `${newFunctionName}(`);
}

export const operatorSeparator = (regex, expression) => {
  return expression.replace(
    regex,
    (match, firstDigit, operator, secondDigit) => {
      return `${firstDigit} ${operator} ${secondDigit}`;
    }
  );
};

// Resolver functions for special cases where changing a function name in expressions isn't enough
export const getOperatorResolver = (operator) => (regex, expression) => {
  return expression.replace(regex, operator);
};

export const getBooleanResolver = (boolString) => (regex, expression) => {
  return expression.replace(regex, boolString);
};

export function ifFunctionResolver(regex, expression) {
  let currentExpression = expression;

  while (currentExpression.match(regex)) {
    const { 0: stringMatch, index: start } = regex.exec(currentExpression);

    const leftString = currentExpression.slice(0, start);
    const rightString = currentExpression.slice(start + stringMatch.length);

    const { stack: parenthesisStack, restString } = readParenthesis({
      string: stringMatch,
      maxStack: 3,
    });

    const [conditionBlock, trueBlock, falseBlock] = parenthesisStack;

    currentExpression = `${leftString}IF(${conditionBlock},${trueBlock},${falseBlock})${restString}${rightString}`;
  }

  return currentExpression;
}

export function modFunctionResolver(regex, expression) {
  let currentExpression = expression;

  while (currentExpression.match(regex)) {
    const { index: start } = regex.exec(currentExpression);

    const { stack: leftParenthesis, restString: leftString } = readParenthesis({
      string: currentExpression,
      start,
      maxStack: 1,
      readBackwards: true,
    });

    const { stack: rightParenthesis, restString: rightString } =
      readParenthesis({
        string: currentExpression,
        start,
        maxStack: 1,
      });

    const [dividend] = leftParenthesis;
    const [divider] = rightParenthesis;

    currentExpression = `${leftString}MOD(${dividend},${divider})${rightString}`;
  }

  return currentExpression;
}

export function isBlankFunctionResolver(regex, expression) {
  let newExpression = expression.replace(regex, `ISBLANK(`);

  const isblankRegex = /ISBLANK\(/g;

  // This function replaced "not(hasvalue(" with "ISBLANK(", so it will leave an additional closing parenthesis ")"
  // The next while loop will remove it

  let match;
  // eslint-disable-next-line no-cond-assign
  while ((match = isblankRegex.exec(newExpression)) != null) {
    const { index } = match;

    for (let i = index; i < newExpression.length; i++) {
      if (newExpression[i] === ')') {
        newExpression = newExpression.slice(0, i) + newExpression.slice(i + 1);
        break;
      }
    }
  }

  return newExpression;
}

/**
 * Resolves conditions in the format (expr [symbol] expr [symbol])
 * Examples:
 *     (1 and 2 and 3 and 4) => AND(1, 2, 3, 4)
 *     (1 or 2 or 3 or 4) => OR(1, 2, 3, 4)
 * @param {string} before
 * @param {string} after
 * @returns string
 */
export const getConditionResolver = (before, after) => (regex, expression) => {
  let currentExpression = expression;

  while (currentExpression.match(regex)) {
    let contentStartIndex;
    let contentEndIndex;
    let parenthesisCount = 0;

    for (let i = currentExpression.indexOf(` ${before} `); i >= 0; i--) {
      if (currentExpression[i] === ')') parenthesisCount -= 1;
      if (currentExpression[i] === '(') parenthesisCount += 1;
      if (parenthesisCount === 1) {
        contentStartIndex = i;
        break;
      }
    }

    parenthesisCount = 0;

    for (
      let i = currentExpression.indexOf(` ${before} `);
      i <= currentExpression.length;
      i++
    ) {
      if (currentExpression[i] === ')') parenthesisCount -= 1;
      if (currentExpression[i] === '(') parenthesisCount += 1;
      if (parenthesisCount === -1) {
        contentEndIndex = i + 1;
        break;
      }
    }

    const functionContentStart = currentExpression.slice(0, contentStartIndex);
    const functionContentEnd = currentExpression.slice(contentEndIndex);
    const functionContent = currentExpression
      .slice(contentStartIndex, contentEndIndex)
      .slice(1, -1);

    const functionParams = functionContent.split(` ${before} `);

    currentExpression = `${functionContentStart}${after}(${functionParams})${functionContentEnd}`;
  }

  return currentExpression;
};

export function removeParenthesisResolver(_, expression) {
  const startsWithParenthesisAndDot = expression.startsWith('(') && expression[1] == ".";
  const endsWithParenthesis = expression.endsWith(')');
  //fixes the case where user wraps the expression in parenthesis, i.e (field_1) * (field_2); DME-3663
  if (startsWithParenthesisAndDot && endsWithParenthesis) {
    return expression; // Return the original expression if it matches the criteria, don't strip the parenthesis
  }
  return expression.slice(1, -1);
}

export function optionFilterResolver(_, expression) {
  // Strings should be removed to use regular expressions correctly
  const storedStringList = [];
  let stringCount = -1;
  let newExpression = expression.replace(/(".*?"|'.*?')/g, (stringMatch) => {
    storedStringList.push(stringMatch);

    stringCount += 1;

    return `[string-${stringCount}]`;
  });

  // This mean that this is filtering by column
  while (newExpression.includes('LOOKUP')) {
    const lookupMatch = newExpression.match(/LOOKUP\(.*?\)/);
    const lookupFunc = lookupMatch[0].slice(7, -1);

    const leftString = newExpression.slice(0, lookupMatch.index);

    const lookupParams = lookupFunc.split(/,\s?/g);

    const columnIdParam = lookupParams[3];

    const rightString = newExpression.match(/LOOKUP\(.+?\)(.+)/)[1];

    newExpression = `${leftString}ASSOCIATED_COLUMN(${columnIdParam})${rightString}`;
  }

  newExpression = newExpression.replace(/\w+?(?=[=|<>])/g, 'OPTION_VALUE()');

  // Merge strings again
  newExpression = newExpression.replace(
    /\[string-(\d)\]/g,
    // eslint-disable-next-line no-unused-vars
    (fullMatch, stringIndex) => {
      return storedStringList[stringIndex];
    }
  );

  return newExpression;
}

// Util functions...

function readParenthesis({
  string,
  start,
  maxStack,
  separator,
  readBackwards,
}) {
  const readingStart =
    start ?? (readBackwards ? string.lastIndexOf(')') : string.indexOf('('));
  let parenthesisCount = 0;
  let blockStart = 0;
  const blockStack = [];
  let restStringOutput;

  for (
    let i = readingStart;
    readBackwards ? i >= 0 : i < string.length;
    readBackwards ? (i -= 1) : (i += 1)
  ) {
    const currentChar = string[i];

    if (currentChar === (readBackwards ? ')' : '(')) {
      if (parenthesisCount === 0) blockStart = i + 1;
      parenthesisCount += 1;
    }

    if (currentChar === (readBackwards ? '(' : ')')) {
      parenthesisCount -= 1;

      const restString = readBackwards
        ? string.slice(0, i)
        : string.slice(i + 1);

      if (parenthesisCount === 0) {
        blockStack.push(
          readBackwards
            ? string.slice(i + 1, blockStart - 1)
            : string.slice(blockStart, i)
        );

        if (blockStack.length === maxStack) {
          restStringOutput = restString;
          break;
        }

        blockStart = 0;
      }

      const nextBlockPos = restString.indexOf('(');
      const currentSeparator = restString.slice(0, nextBlockPos);
      if (separator && !currentSeparator.match(separator)) {
        restStringOutput = restString;
        break;
      }
    }
  }

  return { stack: blockStack, restString: restStringOutput };
}
